'use client';

import React, { useMemo, useState } from "react";
import { loadPlansAsync, listCountries, getCountryPlans, retailForPlan, type EsimPlan } from "@/lib/pricingData";
import { Calculator } from "lucide-react";
import BackgroundDecor from "@/components/BackgroundDecor";
import { useTranslations } from "@/lib/i18n-client";
import { useCurrency } from "@/lib/currency-client";
import DestinationSearch from "@/components/shared/DestinationSearch";

export default function SavingsCalculator() {
  const { t } = useTranslations();
  const { formatUSD } = useCurrency();
  const [allPlans, setAllPlans] = React.useState<EsimPlan[]>([]);
  React.useEffect(() => {
    let alive = true;
    loadPlansAsync().then(p => { if (alive) setAllPlans(p); });
    return () => { alive = false; };
  }, []);
  const countries = useMemo(() => listCountries(allPlans), [allPlans]);
  const [countryCode, setCountryCode] = useState<string>("");
  React.useEffect(() => {
    if (!countryCode && countries.length > 0) setCountryCode(countries[0].code);
  }, [countries, countryCode]);
  const [days, setDays] = useState<number>(7);
  const [dailyRoaming, setDailyRoaming] = useState<number>(10);

  const cheapestRetail = useMemo(() => {
    const cps = getCountryPlans(allPlans, countryCode);
    if (cps.length === 0) return 0;
    const retailPrices = cps.map(p => retailForPlan(p));
    return Math.min(...retailPrices);
  }, [allPlans, countryCode]);

  const roamingCost = useMemo(() => dailyRoaming * days, [dailyRoaming, days]);
  const savings = Math.max(0, roamingCost - cheapestRetail);
  const savingsPct = roamingCost > 0 ? Math.round((savings / roamingCost) * 100) : 0;

  return (
    <section className="py-16 relative pattern-overlay-subtle">
      <BackgroundDecor section="calculator" seed={122} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8">
          <h2 className="text-3xl sm:text-4xl font-bold mb-3">{t("savingsCalculator.title", "Savings Calculator")}</h2>
          <p className="text-xl text-muted-foreground">{t("savingsCalculator.subtitle", "Estimate how much you save vs carrier roaming")}</p>
        </div>

        <div className="glass rounded-2xl p-6 card-pattern overflow-visible relative z-30">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 relative z-20">
            <div className="md:col-span-1">
              <label className="block text-sm text-muted-foreground mb-1">{t("savingsCalculator.fields.destination", "Destination")}</label>
              <DestinationSearch 
                onSelect={(code) => setCountryCode(code)}
                value={countryCode}
                disableScroll={true}
              />
            </div>
            <div>
              <label htmlFor="savings-days" className="block text-sm text-muted-foreground mb-1">{t("savingsCalculator.fields.days", "Trip length (days)")}</label>
              <input
                id="savings-days"
                type="number"
                min={1}
                className="w-full bg-muted text-foreground rounded-md p-3 border border-border"
                value={days}
                onChange={e => setDays(Math.max(1, Number(e.target.value) || 1))}
              />
            </div>
            <div>
              <label htmlFor="savings-daily" className="block text-sm text-muted-foreground mb-1">{t("savingsCalculator.fields.daily", "Roaming $/day")}</label>
              <input
                id="savings-daily"
                type="number"
                min={1}
                className="w-full bg-muted text-foreground rounded-md p-3 border border-border"
                value={dailyRoaming}
                onChange={e => setDailyRoaming(Math.max(1, Number(e.target.value) || 1))}
              />
            </div>
          </div>

          <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4 items-stretch relative z-10">
            <div className="glass rounded-xl p-4">
              <div className="text-sm text-muted-foreground">{t("savingsCalculator.labels.roaming", "Carrier roaming")}</div>
              <div className="text-2xl font-bold">{formatUSD(roamingCost)}</div>
            </div>
            <div className="glass rounded-xl p-4">
              <div className="text-sm text-muted-foreground">{t("savingsCalculator.labels.cheapest", "Cheapest eSIMSupra plan")}</div>
              <div className="text-2xl font-bold">{formatUSD(cheapestRetail || 0)}</div>
            </div>
            <div className="glass rounded-xl p-4 flex items-center gap-3">
              <Calculator className="w-6 h-6 text-primary" />
              <div>
                <div className="text-sm text-muted-foreground">{t("savingsCalculator.labels.savings", "Estimated savings")}</div>
                <div className="text-2xl font-bold text-success">{formatUSD(savings)} <span className="text-sm text-muted-foreground">({savingsPct}%)</span></div>
              </div>
            </div>
          </div>

          <div className="mt-3 text-xs text-muted-foreground text-center">{t("savingsCalculator.footer", "Estimate only. Actual plan choice and usage may vary.")}</div>
        </div>
      </div>
    </section>
  );
}
